"use client"

import { useEffect, useMemo, useState } from "react"
import { ref as storageRef, getDownloadURL } from "firebase/storage"
import { storage } from "../../Firebase/firestore" // Your Firebase storage instance
import {
  FaFileDownload,
  FaFileAlt,
  FaFolder,
  FaEye,
  FaShareAlt,
  FaSpinner,
} from "react-icons/fa"

const DocumentsSection = ({ selectedUser, userDocuments, docsLoading = false }) => {
  const brands = useMemo(() => Object.keys(userDocuments || {}), [userDocuments])
  const hasDocuments = brands.length > 0

  const [activeBrand, setActiveBrand] = useState("")
  const [selectedDocs, setSelectedDocs] = useState({})

  useEffect(() => {
    if (brands.length > 0 && !activeBrand) {
      setActiveBrand(brands[0])
    }
  }, [brands, activeBrand])

  const sitesInBrand = userDocuments?.[activeBrand] || {}

  const toggleDocSelection = (site, fileName, filePath) => {
    const key = `${activeBrand}/${site}/${fileName}`
    setSelectedDocs((prev) => {
      if (prev[key]) {
        const copy = { ...prev }
        delete copy[key]
        return copy
      }
      return {
        ...prev,
        [key]: { site, name: fileName, path: filePath },
      }
    })
  }

  const handleShare = () => {
    const files = Object.values(selectedDocs)
    if (files.length === 0) {
      alert("Please select at least one document to share.")
      return
    }

    const shareText = files.map(f => `${f.name}\n${f.path}`).join("\n\n")
    if (navigator.share) {
      navigator.share({ title: "Shared Documents", text: shareText }).catch(console.error)
    } else {
      const mailto = `mailto:?subject=Shared Documents&body=${encodeURIComponent(shareText)}`
      window.open(mailto, "_blank")
    }
  }

  // Safely handle path or URL
  const handleViewFile = async (filePath) => {
    try {
      if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
        // Direct URL
        window.open(filePath, "_blank")
      } else {
        // Treat as Firebase storage path
        const url = await getDownloadURL(storageRef(storage, filePath))
        window.open(url, "_blank")
      }
    } catch (err) {
      console.error("Failed to open file:", err)
      alert("Unable to open file.")
    }
  }

  if (docsLoading) {
    return (
      <div className="empty-state">
        <FaSpinner className="spinner" />
        <p>Fetching documents, please wait...</p>
      </div>
    )
  }

  if (!docsLoading && !hasDocuments) {
    return (
      <div className="empty-state">
        <FaFileAlt className="empty-icon" />
        <p>No documents found</p>
      </div>
    )
  }

  return (
    <div className="documents-tab">
      <div className="documents-header">
        <FaFileDownload />
        <div>
          <h2>User Documents</h2>
          <p>Documents for {selectedUser?.username || selectedUser?.email}</p>
        </div>
      </div>

      <div className="documents-layout">
        <aside className="documents-sidebar">
          <h4>Brands</h4>
          {brands.map((brand) => (
            <div
              key={brand}
              className={`folder-item ${brand === activeBrand ? "active" : ""}`}
              onClick={() => {
                setActiveBrand(brand)
                setSelectedDocs({})
              }}
            >
              <FaFolder />
              <span>{brand}</span>
            </div>
          ))}
        </aside>

        <section className="documents-content">
          <div className="documents-toolbar">
            <h3>{activeBrand}</h3>
            <button
              className="share-btn"
              disabled={Object.keys(selectedDocs).length === 0}
              onClick={handleShare}
            >
              <FaShareAlt /> Share Selected
            </button>
          </div>

          <div className="sites-grid">
            {Object.entries(sitesInBrand).map(([siteName, docs]) => (
              <div key={siteName} className="site-card">
                <h4 className="site-title">{siteName}</h4>
                <div className="documents-grid">
                  {Object.entries(docs).map(([docName, filePath]) => {
                    const key = `${activeBrand}/${siteName}/${docName}`
                    return (
                      <div key={key} className="document-card">
                        <input
                          type="checkbox"
                          checked={!!selectedDocs[key]}
                          onChange={() => toggleDocSelection(siteName, docName, filePath)}
                        />
                        <FaFileAlt className="doc-icon" />
                        <p title={docName}>{docName}</p>
                        <button className="view-btn" onClick={() => handleViewFile(filePath)}>
                          <FaEye /> View
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default DocumentsSection